import { useState, type FormEvent } from "react";
import { api } from "../api.js";
import { useAuth } from "../auth.js";
import { DesktopDownloadCta } from "../DesktopDownloadCta.js";
import { HeroBrand, LocaleToggle, PasswordField, ThemeToggle, useLocale } from "../ui.js";
import type { AuthView } from "../App.js";

export function SignupPage({ onNavigate }: { onNavigate: (v: AuthView) => void }) {
  const { tr } = useLocale();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [emailTaken, setEmailTaken] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInfo(null);
    setEmailTaken(false);
    const res = await api.signup(email.trim(), password);
    setBusy(false);
    if (res.ok && res.data?.session && res.data.account) {
      setSession(res.data.account);
      return;
    }
    if (res.ok && res.data?.denialCode === "pending_approval") {
      setInfo(tr("pendingApproval"));
      return;
    }
    const denial = res.data?.denialCode ?? res.denialCode;
    if (denial === "signup_disabled") {
      setInfo(tr("signupClosed"));
      return;
    }
    if (denial === "email_taken") {
      setError(tr("emailTaken"));
      setEmailTaken(true);
      return;
    }
    if (denial === "invalid_email") {
      setError(tr("invalidEmail"));
      return;
    }
    if (denial === "weak_password") {
      setError(tr("weakPassword"));
      return;
    }
    setError(tr("genericError"));
  }

  return (
    <main className="ss-login">
      <HeroBrand />
      <div className="ss-login-form-wrap">
        <form className="ss-login-card" onSubmit={submit}>
          <div className="ss-login-toprow">
            <ThemeToggle />
            <LocaleToggle />
          </div>
          <h2>{tr("signup")}</h2>
          <p className="sub">{tr("signupSub")}</p>
          <div className="ss-field">
            <label htmlFor="su-email">{tr("email")}</label>
            <input id="su-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <PasswordField id="su-password" label={tr("password")} value={password} onChange={setPassword} autoComplete="new-password" minLength={8} />
          <p className="muted">{tr("passwordHint")}</p>
          {error ? <div className="msg error">{error}</div> : null}
          {info ? <div className="msg info">{info}</div> : null}
          {emailTaken ? (
            <div className="btn-row">
              <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("login")}>
                {tr("toLogin")}
              </button>
              <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("forgot")}>
                {tr("forgot")}
              </button>
            </div>
          ) : null}
          <button className="ss-login-submit" type="submit" disabled={busy}>
            {tr("signup")}
          </button>
          <div className="btn-row">
            <button type="button" className="ss-btn-ghost" onClick={() => onNavigate("login")}>
              {tr("toLogin")}
            </button>
          </div>
          <DesktopDownloadCta />
        </form>
      </div>
    </main>
  );
}
